/**
 * Projects Service - chamadas de API para o módulo Projetos
 *
 * Envelope de resposta do backend: `{ success, data, message? }` — sempre
 * extrair `response.data.data` (ver `ticketsService.ts` como referência).
 */

import { api } from '@/lib/api';
import type {
  CreateProjectRequest,
  CreateProjectTaskRequest,
  LinkTicketRequest,
  ProjectDropdownItem,
  ProjectGanttResponse,
  ProjectListResponse,
  ProjectQuery,
  ProjectResponse,
  ProjectTaskResponse,
  TaskDependencyStatusResponse,
  UpdateProjectRequest,
  UpdateProjectTaskRequest,
} from '../types';

export const projectsService = {
  /**
   * Listagem paginada de projetos com métricas agregadas (progresso, atraso,
   * próximo marco) — `GET /api/projects`.
   */
  async getProjects(query?: ProjectQuery): Promise<ProjectListResponse> {
    const response = await api.get('projects', { params: query });
    return response.data.data;
  },

  /**
   * Busca projeto por id — `GET /api/projects/:id`.
   * Backend gateia por module scope (`assertProjectAccess`); fora do escopo
   * retorna 404.
   */
  async getProjectById(id: string): Promise<ProjectResponse> {
    const response = await api.get(`projects/${id}`);
    return response.data.data;
  },

  /**
   * Cronograma (Gantt) do projeto: tarefas enriquecidas (ticket/stage/user) +
   * progresso/bloqueios — `GET /api/projects/:id/gantt`.
   */
  async getGantt(id: string): Promise<ProjectGanttResponse> {
    const response = await api.get(`projects/${id}/gantt`);
    return response.data.data;
  },

  /**
   * Criação composta: projeto + fases + tarefas materializadas como tickets —
   * `POST /api/projects`.
   */
  async createProject(data: CreateProjectRequest): Promise<ProjectResponse> {
    const response = await api.post('projects', data);
    return response.data.data;
  },

  /**
   * Atualiza dados do projeto (nome, cor, líder, membros, fases, status...) —
   * `PUT /api/projects/:id`.
   */
  async updateProject(id: string, data: UpdateProjectRequest): Promise<ProjectResponse> {
    const response = await api.put(`projects/${id}`, data);
    return response.data.data;
  },

  /**
   * Exclui o projeto (desvincula tickets + soft-delete de tarefas) —
   * `DELETE /api/projects/:id`.
   */
  async deleteProject(id: string): Promise<void> {
    await api.delete(`projects/${id}`);
  },

  /**
   * Cria uma nova tarefa (materializa ticket novo) —
   * `POST /api/projects/:id/tasks`.
   */
  async addTask(projectId: string, data: CreateProjectTaskRequest): Promise<ProjectTaskResponse> {
    const response = await api.post(`projects/${projectId}/tasks`, data);
    return response.data.data;
  },

  /**
   * Vincula um ticket já existente como tarefa do projeto —
   * `POST /api/projects/:id/tasks/link`.
   */
  async linkTicket(projectId: string, data: LinkTicketRequest): Promise<ProjectTaskResponse> {
    const response = await api.post(`projects/${projectId}/tasks/link`, data);
    return response.data.data;
  },

  /**
   * Atualiza uma tarefa (datas, fase, dependências, marco) —
   * `PUT /api/projects/:id/tasks/:taskId`.
   */
  async updateTask(
    projectId: string,
    taskId: string,
    data: UpdateProjectTaskRequest,
  ): Promise<ProjectTaskResponse> {
    const response = await api.put(`projects/${projectId}/tasks/${taskId}`, data);
    return response.data.data;
  },

  /**
   * Remove uma tarefa (soft delete) e desvincula o ticket do projeto —
   * `DELETE /api/projects/:id/tasks/:taskId`.
   */
  async removeTask(projectId: string, taskId: string): Promise<void> {
    await api.delete(`projects/${projectId}/tasks/${taskId}`);
  },

  /**
   * Lista leve para selectors/dropdowns cross-module — `GET /api/projects/dropdown`
   * (sem `permissionMiddleware`).
   */
  async getForDropdown(): Promise<ProjectDropdownItem[]> {
    const response = await api.get('projects/dropdown');
    return response.data.data;
  },

  /**
   * Estado de dependências de um ticket (consumido pelo kanban de tickets,
   * modo 'warn') — `GET /api/projects/task-status/:ticketId` (sem
   * `permissionMiddleware`).
   */
  async getTaskStatusByTicket(ticketId: string): Promise<TaskDependencyStatusResponse> {
    const response = await api.get(`projects/task-status/${ticketId}`);
    return response.data.data;
  },
};
